import { diameterFromGirthCm } from '@treetrack/protocol';
import { growthBySpecies, treeTimeline } from '../aggregate';
import { dateTicks, dateX, lineChartSvg } from '../charts';
import { fmtDate, fmtNum, h, svgNode } from '../dom';
import { signedMediaUrl, type ProjectBundle } from '../data';
import { L, speciesName, t } from '../i18n';
import { toHash } from '../router';
import { card, conditionBadge, empty, table } from './shared';

export function treeView(b: ProjectBundle, treeId: string): HTMLElement {
  const tree = b.trees.find((x) => x.id === treeId);
  const root = h('div', { class: 'tree' });
  root.appendChild(h('a', { class: 'back', href: toHash({ name: 'project', id: b.project.id }) }, `← ${b.project.name}`));
  if (!tree) {
    root.appendChild(empty(t('common.none')));
    return root;
  }
  const species = tree.species_id ? b.species.find((s) => s.id === tree.species_id) : null;
  const zone = tree.zone_id ? b.zones.find((z) => z.id === tree.zone_id) : null;
  const latest = b.latest.get(tree.id);
  const timeline = treeTimeline(tree.id, b.measurements, b.surveys);

  root.appendChild(
    h(
      'div',
      { class: 'project-head' },
      h('h1', null, tree.tag, ' ', conditionBadge(latest?.condition)),
      h(
        'div',
        { class: 'meta' },
        `${t('field.species')}: ${speciesName(species)}`,
        ' · ',
        `${t('field.zone')}: ${zone?.label ?? '—'}`,
        ' · ',
        `${t('tree_status.' + tree.status)}`,
        tree.planted_on ? ` · ${t('field.planted_on')}: ${fmtDate(tree.planted_on)}` : '',
        tree.lat != null && tree.lng != null ? ` · ${tree.lat.toFixed(5)}, ${tree.lng.toFixed(5)} (± ${fmtNum(tree.gps_accuracy_m, 0)} m)` : '',
        tree.grid_row != null ? ` · ${t('field.grid')}: ${tree.grid_row}/${tree.grid_col ?? '—'}` : '',
      ),
    ),
  );

  // Flags
  const flag = b.flags.find((f) => f.tree_id === tree.id);
  const raised = flag ? (['stressed_twice', 'not_found_last', 'not_found_twice', 'possible_mis_tag'] as const).filter((k) => flag[k]) : [];
  if (raised.length) root.appendChild(card(t('tree.flags'), h('ul', { class: 'flag-list' }, ...raised.map((k) => h('li', null, t(`tree.flag_${k}`))))));

  // Growth chart with species median overlay
  if (timeline.length) {
    const dates = timeline.map((e) => e.date);
    const own = {
      height: { label: `${t('dashboard.height')} (cm)`, color: '#1b5e20', points: timeline.filter((e) => e.measurement.height_cm != null).map((e) => ({ x: dateX(e.date), y: Number(e.measurement.height_cm) })) },
      diameter: { label: `${t('dashboard.diameter')} (mm)`, color: '#f9a825', points: timeline.filter((e) => e.diameterBasal != null).map((e) => ({ x: dateX(e.date), y: Math.round((e.diameterBasal as number) * 100) / 10 })) },
    };
    const spGrowth = growthBySpecies(b.measurements, b.trees, b.surveys, b.species).find((g) => g.species_id === tree.species_id);
    const medianH = spGrowth
      ? { label: `${t('field.species')} ${t('dashboard.median')}`, color: '#90caf9', dashed: true, points: spGrowth.points.filter((p) => p.medianHeightCm != null).map((p) => ({ x: dateX(p.date), y: p.medianHeightCm as number })) }
      : null;
    const medianD = spGrowth
      ? { label: `${t('field.species')} ${t('dashboard.median')}`, color: '#90caf9', dashed: true, points: spGrowth.points.filter((p) => p.medianDiameterCm != null).map((p) => ({ x: dateX(p.date), y: Math.round((p.medianDiameterCm as number) * 100) / 10 })) }
      : null;
    root.appendChild(
      card(
        t('tree.growth'),
        h(
          'div',
          { class: 'charts' },
          h('div', { class: 'chart-box' }, h('h3', null, t('dashboard.height')), svgNode(lineChartSvg([own.height, ...(medianH ? [medianH] : [])], { xTicks: dateTicks(dates) }))),
          h('div', { class: 'chart-box' }, h('h3', null, `${t('dashboard.diameter')} (10 cm)`), svgNode(lineChartSvg([own.diameter, ...(medianD ? [medianD] : [])], { xTicks: dateTicks(dates) }))),
        ),
      ),
    );
  }

  // Timeline
  root.appendChild(
    card(
      t('tree.timeline'),
      timeline.length
        ? table(
            [t('field.date'), L('Cond.', 'Cond.'), t('field.height_cm'), t('field.girth_at', { height: 10 }), t('field.diameter_cm'), t('field.girth_at', { height: 130 }), t('field.soil_check'), t('field.observations'), t('field.note'), t('field.team')],
            timeline
              .slice()
              .reverse()
              .map((e) => [
                fmtDate(e.date),
                conditionBadge(e.measurement.condition),
                fmtNum(e.measurement.height_cm, 0) + (e.measurement.height_method ? ` (${t('height_method.' + e.measurement.height_method)})` : ''),
                fmtNum(e.girthBasal, 1),
                e.girthBasal != null ? fmtNum(diameterFromGirthCm(e.girthBasal), 2) : '—',
                e.girthDbh != null ? `${fmtNum(e.girthDbh, 1)} (⌀ ${fmtNum(e.diameterDbh, 2)})` : '—',
                [e.measurement.soil_moisture && t('soil_moisture.' + e.measurement.soil_moisture), e.measurement.soil_temp && t('soil_temp.' + e.measurement.soil_temp)].filter(Boolean).join(', ') || '—',
                e.measurement.chips.map((c) => t('chip.' + c)).join(', ') || '—',
                e.measurement.note ?? '—',
                e.team_name ?? '—',
              ]),
          )
        : empty(t('tree.no_measurements')),
    ),
  );

  // Photos
  const photos = b.media.filter((m) => m.tree_id === tree.id && m.storage_path).sort((a, c) => (a.taken_at ?? a.uploaded_at).localeCompare(c.taken_at ?? c.uploaded_at));
  const strip = h('div', { class: 'photo-strip' }, photos.length ? null : empty(t('common.none')));
  root.appendChild(card(t('tree.photos'), strip));
  for (const m of photos) {
    const fig = h('figure', { class: 'photo' }, h('figcaption', null, fmtDate(m.taken_at ?? m.uploaded_at)));
    strip.appendChild(fig);
    void signedMediaUrl(m.storage_path as string).then((url) => {
      if (url) fig.prepend(h('img', { src: url, alt: `${tree.tag} ${fmtDate(m.taken_at)}`, loading: 'lazy' }));
    });
  }

  // Interventions
  const ivs = b.interventions.filter((i) => i.tree_id === tree.id);
  root.appendChild(
    card(
      t('tree.interventions'),
      ivs.length
        ? table(
            [t('field.date'), L('Tipo', 'Type'), t('field.quantity'), t('field.note')],
            ivs.map((i) => [fmtDate(i.done_at), t('intervention.' + i.type), i.quantity != null ? `${fmtNum(i.quantity, 1)} ${i.unit ? t('unit.' + i.unit) : ''}` : '—', i.note ?? '—']),
          )
        : empty(t('common.none')),
    ),
  );

  return root;
}
